import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

interface Quote {
  text: string;
  author: string;
}

const QUOTES: Quote[] = [
  {
    text: "We’re here to help each other get through this thing, whatever it is.",
    author: "Kurt Vonnegut"
  },
  {
    text: "Computers are useless. They can only give you answers.",
    author: "Pablo Picasso"
  },
  {
    text: "We become what we behold. We shape our tools and then our tools shape us.",
    author: "Marshall McLuhan"
  },
  {
    text: "The real problem is not whether machines think but whether people do.",
    author: "B. F. Skinner"
  },
  {
    text: "A tool is something that amplifies human capability. If it replaces it entirely, we should be suspicious.",
    author: "Jaron Lanier"
  }
];

const Hero: React.FC = () => {
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [driftDirection, setDriftDirection] = useState(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setDriftDirection(prev => prev * -1);
      
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % QUOTES.length);
        setFade(true);
      }, 1200);
    }, 10000); 

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="min-h-[80vh] flex flex-col items-center justify-center px-6 pt-32 pb-20 bg-cream text-center relative overflow-hidden">
      <div className="animate-fade-in-up w-full max-w-7xl mx-auto z-10">
        <h1 className="text-7xl md:text-9xl lg:text-[13rem] mb-4 tracking-tighter font-black text-charcoal leading-[0.85] selection:bg-charcoal selection:text-white">
          VIBE WITH AI
        </h1>
        
        <h2 className="text-xl md:text-3xl lg:text-4xl mb-12 tracking-tight font-bold text-neutral-400 uppercase">
          Sensemaking in the Algorithmic Age
        </h2>

        <div className="flex flex-col items-center gap-8 mb-16">
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="#signup" 
              className="bg-terracotta text-white text-[10px] font-black uppercase tracking-[0.4em] px-10 py-5 rounded-full hover:bg-charcoal transition-all shadow-[0_15px_30px_-5px_rgba(192,108,93,0.3)]"
            >
              Get Notified for Salon II
            </a>
            <button 
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'archive' }))}
              className="group flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] text-charcoal px-10 py-5 rounded-full border-2 border-charcoal/20 hover:border-terracotta hover:text-terracotta transition-all"
            >
              See Salon I Recap
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto h-48 flex flex-col items-center justify-center relative">
          <div 
            className={`transition-all duration-[1500ms] ease-[cubic-bezier(0.23,1,0.32,1)] transform ${
              fade 
                ? 'opacity-100 translate-y-0 translate-x-0 scale-100 blur-0' 
                : `opacity-0 -translate-y-12 ${driftDirection > 0 ? 'translate-x-8' : '-translate-x-8'} scale-110 blur-md`
            }`}
          >
            <p className="text-2xl md:text-4xl font-medium text-neutral-800 leading-tight px-4 mb-8 italic tracking-tight max-w-3xl mx-auto">
              "{QUOTES[index].text}"
            </p>
            <div className="h-px w-24 bg-terracotta mx-auto mb-6 opacity-40" />
            <p className="text-[10px] uppercase tracking-[0.5em] font-black text-neutral-500">
              {QUOTES[index].author}
            </p>
          </div>
          
          <div className={`absolute inset-0 bg-terracotta/5 blur-[100px] rounded-full transition-all duration-[2000ms] ${
            fade ? 'opacity-100 scale-100' : 'opacity-0 scale-150'
          }`} />
        </div>
      </div>
    </section>
  );
};

export default Hero;